import React, { useState } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Box,
  TablePagination,
} from "@mui/material";
import {
  AddListingButton,
  EmptyTableRow,
  ListingPageShell,
  bodyCellSx,
  formatListingDate,
  headCellSx,
} from "../AssetListing/assetListingShared";
import { DARK_NAVY, PROFILE_FONT } from "../../constants/layout";
import RegisterForm from "./AddRM";

interface User {
  id: number;
  firstName: string;
  email: string;
  mobile: string;
  createdAt: string;
  aumUserId: string;
}

interface RMTableProps {
  users: User[];
  onChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  onCancel: () => void;
  totalPages: number;
  pageSize: number;
  onPageChange: (
    event: React.MouseEvent<HTMLButtonElement> | null,
    newPage: number
  ) => void;
  onPageSizeChange: (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => void;
  currentPage: number;
  searchQuery: string;
  setSearchQuery: (query: string) => void;
  onRefresh: () => void;
}

const RMTable: React.FC<RMTableProps> = ({
  users,
  totalPages,
  currentPage,
  pageSize,
  onPageChange,
  onPageSizeChange,
  searchQuery,
  setSearchQuery,
  onRefresh,
}) => {
  const [showAddModal, setShowAddModal] = useState(false);

  const handleAddSuccess = () => {
    setShowAddModal(false);
    onRefresh();
  };

  return (
    <>
      <ListingPageShell
        title="Relation Managers"
        subtitle="View and manage all registered relation managers."
        searchValue={searchQuery}
        onSearchChange={setSearchQuery}
        searchPlaceholder="Search by name, email or user ID..."
        action={
          <AddListingButton label="Add Manager" onClick={() => setShowAddModal(true)} />
        }
      >
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell sx={headCellSx}>Name</TableCell>
                <TableCell sx={headCellSx}>User ID</TableCell>
                <TableCell sx={headCellSx}>Email</TableCell>
                <TableCell sx={headCellSx}>Mobile</TableCell>
                <TableCell sx={headCellSx}>Registered At</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {users?.length ? (
                users.map((user) => (
                  <TableRow key={user.id} hover sx={{ "&:hover": { bgcolor: "#FAFAFA" } }}>
                    <TableCell sx={bodyCellSx}>{user.firstName}</TableCell>
                    <TableCell sx={{ ...bodyCellSx, color: "#5A6B7D" }}>{user.aumUserId}</TableCell>
                    <TableCell sx={{ ...bodyCellSx, color: "#5A6B7D" }}>{user.email}</TableCell>
                    <TableCell sx={{ ...bodyCellSx, color: "#5A6B7D" }}>{user.mobile}</TableCell>
                    <TableCell sx={{ ...bodyCellSx, color: "#5A6B7D" }}>
                      {formatListingDate(user.createdAt)}
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <EmptyTableRow colSpan={5} message="No relation managers found" />
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>

        <Box sx={{ borderTop: "1px solid #F5F5F5" }}>
          <TablePagination
            component="div"
            rowsPerPageOptions={[10, 20, 50, 100]}
            count={totalPages * pageSize}
            rowsPerPage={pageSize}
            page={currentPage - 1}
            onPageChange={onPageChange}
            onRowsPerPageChange={onPageSizeChange}
            labelDisplayedRows={() => `${currentPage} – ${totalPages}`}
            sx={{
              fontFamily: PROFILE_FONT,
              color: DARK_NAVY,
              ".MuiTablePagination-selectLabel, .MuiTablePagination-displayedRows": {
                fontFamily: PROFILE_FONT,
                fontSize: 13,
              },
            }}
          />
        </Box>
      </ListingPageShell>

      <RegisterForm
        open={showAddModal}
        onClose={() => setShowAddModal(false)}
        onSuccess={handleAddSuccess}
      />
    </>
  );
};

export default RMTable;
